import {
  CurrencyCode,
  enumCurrencyCode,
  enumInvoiceEntityReferenceType,
  enumUnit,
  InvoiceEntityReferenceType,
  Money,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { weightToPounds } from '@rematter/ui-web';

import {
  FreightChargeForm,
  ReceivedLineItemForm,
  ReconciliationDeductionForm,
} from '../_state/loadReconciliation.state';
import { enumLoadWeightType, LoadFormLineItem } from '../types/load';

import { convertWeightForPricing } from './weight';

import { enumLineItemGroupName } from '~/shared/components/line-items/types';

/**
 * Input type for transforming load line items to invoice line items
 */
export type LoadLineItemForInvoice = {
  id: string;
  itemId: string | null;
  itemName: string;
  weights: Record<string, { value: number; unit: string }> | null;
  unitPrice: { amount: number; currencyCode: CurrencyCode } | null;
  pricingUnit: string | null;
};

/**
 * Output type for invoice line item (matches GraphQL InvoiceLineItemInput)
 */
export type InvoiceLineItemInput = {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: { amount: number; currencyCode: CurrencyCode };
  totalPrice: { amount: number; currencyCode: CurrencyCode };
  itemId?: string;
  groupName?: string;
  sortOrder?: number;
  sourceReference?: {
    entityId: string;
    entityType: InvoiceEntityReferenceType;
  };
};

/**
 * Transforms a single load line item to an invoice line item
 */
function transformSingleLineItem(
  lineItem: LoadLineItemForInvoice,
  sortOrder: number,
  groupName?: string,
): InvoiceLineItemInput | null {
  const netWeight = lineItem.weights?.[enumLoadWeightType.Net];
  const unitPrice = lineItem.unitPrice;

  if (!netWeight?.value || !unitPrice) {
    return null;
  }

  const currencyCode = unitPrice.currencyCode;
  const pricingUnit = lineItem.pricingUnit ?? enumUnit.Pounds;

  // Convert weight to pricing unit for calculation
  const netWeightInPounds = weightToPounds(netWeight as Weight);
  const quantityInPricingUnit = convertWeightForPricing(
    netWeightInPounds,
    pricingUnit,
  );

  // Calculate total price
  const totalAmount = quantityInPricingUnit * unitPrice.amount;

  return {
    description: lineItem.itemName || 'Unknown Item',
    quantity: quantityInPricingUnit,
    unit: pricingUnit,
    unitPrice: {
      amount: unitPrice.amount,
      currencyCode,
    },
    totalPrice: {
      amount: totalAmount,
      currencyCode,
    },
    ...(lineItem.itemId && { itemId: lineItem.itemId }),
    ...(groupName && { groupName }),
    sortOrder,
    sourceReference: {
      entityId: lineItem.id,
      entityType: enumInvoiceEntityReferenceType.OutboundLoadLineItem,
    },
  };
}

/**
 * Transforms an array of load line items to invoice line items
 * Optionally groups them under a single groupName (e.g., "Load #123")
 *
 * @param lineItems - Array of load line items to transform
 * @param groupName - Optional group name for all line items (e.g., "Load #123")
 * @param startingSortOrder - Starting sort order (default: 0)
 * @returns Array of invoice line items
 */
export function transformLoadLineItemsToInvoiceLineItems(
  lineItems: LoadLineItemForInvoice[],
  groupName?: string,
  startingSortOrder = 0,
): InvoiceLineItemInput[] {
  const result: InvoiceLineItemInput[] = [];

  lineItems.forEach((lineItem, index) => {
    const invoiceLineItem = transformSingleLineItem(
      lineItem,
      startingSortOrder + index,
      groupName,
    );

    if (invoiceLineItem) {
      result.push(invoiceLineItem);
    }
  });

  return result;
}

/**
 * Transforms load line items from multiple loads into invoice line items
 * Groups line items by load, with each load's document number as the group name
 *
 * @param loads - Array of loads with their line items
 * @returns Array of invoice line items with groupName set to load number
 */
export function transformMultipleLoadsToInvoiceLineItems(
  loads: Array<{
    documentNumber: number | string;
    lineItems: LoadLineItemForInvoice[];
  }>,
): InvoiceLineItemInput[] {
  const result: InvoiceLineItemInput[] = [];
  let currentSortOrder = 0;

  loads.forEach((load) => {
    const groupName = `Load #${load.documentNumber}`;
    const loadLineItems = transformLoadLineItemsToInvoiceLineItems(
      load.lineItems,
      groupName,
      currentSortOrder,
    );

    result.push(...loadLineItems);
    currentSortOrder += loadLineItems.length;
  });

  return result;
}

/**
 * Calculates the total amount from an array of invoice line items
 *
 * @param lineItems - Array of invoice line items
 * @returns Total amount (sum of all totalPrice amounts)
 */
export function calculateInvoiceTotal(
  lineItems: InvoiceLineItemInput[],
): number {
  return lineItems.reduce((sum, item) => sum + item.totalPrice.amount, 0);
}

/**
 * Extracts the currency code from invoice line items
 * Throws error if currencies are mixed
 *
 * @param lineItems - Array of invoice line items
 * @returns Currency code
 * @throws Error if line items have mixed currencies
 */
export function getInvoiceCurrencyCode(
  lineItems: InvoiceLineItemInput[],
): CurrencyCode {
  if (lineItems.length === 0) {
    return enumCurrencyCode.USD;
  }

  const firstCurrency = lineItems[0].totalPrice.currencyCode;
  const allSameCurrency = lineItems.every(
    (item) => item.totalPrice.currencyCode === firstCurrency,
  );

  if (!allSameCurrency) {
    throw new Error(
      'Cannot create invoice with mixed currencies. All line items must have the same currency.',
    );
  }

  return firstCurrency;
}

/**
 * Validates that all invoice line items have the same currency
 *
 * @param lineItems - Array of invoice line items
 * @returns True if all items have the same currency, false otherwise
 */
export function validateInvoiceCurrency(
  lineItems: InvoiceLineItemInput[],
): boolean {
  if (lineItems.length <= 1) {
    return true;
  }

  const firstCurrency = lineItems[0].totalPrice.currencyCode;
  return lineItems.every(
    (item) => item.totalPrice.currencyCode === firstCurrency,
  );
}

export function transformDeductionsToInvoiceLineItems(
  deductions: ReconciliationDeductionForm[],
  deductionTypesMap: Map<string, string>,
  currencyCode: CurrencyCode = enumCurrencyCode.USD,
  startingSortOrder = 0,
): InvoiceLineItemInput[] {
  const result: InvoiceLineItemInput[] = [];

  deductions.forEach((deduction, index) => {
    const amount = deduction.priceQuantity?.amount ?? 0;
    if (amount === 0) return;

    const deductionCurrency =
      (deduction.priceQuantity as Money | null)?.currencyCode ?? currencyCode;
    const typeName =
      deductionTypesMap.get(deduction.deductionTypeId) ?? 'Deduction';

    result.push({
      description: typeName,
      quantity: 1,
      unit: enumUnit.Each,
      unitPrice: { amount: -amount, currencyCode: deductionCurrency },
      totalPrice: { amount: -amount, currencyCode: deductionCurrency },
      groupName: enumLineItemGroupName.Deductions,
      sortOrder: startingSortOrder + index,
    });
  });

  return result;
}

export function transformFreightChargesToInvoiceLineItems(
  freightCharges: FreightChargeForm[],
  currencyCode: CurrencyCode = enumCurrencyCode.USD,
  startingSortOrder = 0,
): InvoiceLineItemInput[] {
  const result: InvoiceLineItemInput[] = [];

  freightCharges.forEach((charge, index) => {
    const accrued = charge.accruedAmount?.amount ?? 0;
    const chargeback = charge.chargebackAmount?.amount ?? 0;
    const netAmount = accrued - chargeback;
    if (netAmount === 0) return;

    const chargeCurrency = charge.accruedAmount?.currencyCode ?? currencyCode;
    const description =
      [charge.hauler, charge.freightLane].filter(Boolean).join(' - ') ||
      'Freight Charge';

    result.push({
      description,
      quantity: 1,
      unit: enumUnit.Each,
      unitPrice: { amount: netAmount, currencyCode: chargeCurrency },
      totalPrice: { amount: netAmount, currencyCode: chargeCurrency },
      groupName: enumLineItemGroupName.FreightCharges,
      sortOrder: startingSortOrder + index,
    });
  });

  return result;
}

export function transformReceivedLineItemsToInvoiceLineItems(
  receivedLineItems: ReceivedLineItemForm[],
  groupName?: string,
  startingSortOrder = 0,
): InvoiceLineItemInput[] {
  const result: InvoiceLineItemInput[] = [];

  receivedLineItems.forEach((item, index) => {
    const grossValue = item.grossWeight?.value ?? 0;
    const tareValue = item.tareWeight?.value ?? 0;
    const netWeightInStorageUnit = grossValue - tareValue;
    const unitPrice = item.unitPrice;

    if (netWeightInStorageUnit <= 0 || !unitPrice) return;

    const currencyCode = unitPrice.currencyCode;
    const pricingUnit = item.pricingUnit ?? enumUnit.Pounds;
    const weightUnit = item.grossWeight?.unit ?? enumUnit.Pounds;

    let quantityInPricingUnit: number;
    if (weightUnit === pricingUnit) {
      quantityInPricingUnit = netWeightInStorageUnit;
    } else {
      const netWeightInPounds = weightToPounds({
        value: netWeightInStorageUnit,
        unit: weightUnit,
        __typename: 'Weight',
      } as Weight);
      quantityInPricingUnit = convertWeightForPricing(
        netWeightInPounds,
        pricingUnit,
      );
    }

    const totalAmount = quantityInPricingUnit * unitPrice.amount;

    result.push({
      description: item.materialType || 'Unknown Item',
      quantity: quantityInPricingUnit,
      unit: pricingUnit,
      unitPrice: { amount: unitPrice.amount, currencyCode },
      totalPrice: { amount: totalAmount, currencyCode },
      ...(item.itemId && { itemId: item.itemId }),
      ...(groupName && { groupName }),
      sortOrder: startingSortOrder + index,
      sourceReference: {
        entityId: item.loadLineItemId,
        entityType: enumInvoiceEntityReferenceType.OutboundLoadLineItem,
      },
    });
  });

  return result;
}

export type BuildInvoiceLineItemsParams = {
  invoiceType: string;
  receivedLineItems: ReceivedLineItemForm[];
  shippedLineItems: LoadFormLineItem[];
  deductions: ReconciliationDeductionForm[];
  freightCharges: FreightChargeForm[];
  deductionTypesMap: Map<string, string>;
  loadNumber?: string;
};

export function buildInvoiceLineItemsFromReconciliation({
  invoiceType,
  receivedLineItems,
  shippedLineItems,
  deductions,
  freightCharges,
  deductionTypesMap,
  loadNumber,
}: BuildInvoiceLineItemsParams): InvoiceLineItemInput[] {
  const groupName = loadNumber ? `Load #${loadNumber}` : undefined;

  let materialLineItems: InvoiceLineItemInput[];
  if (invoiceType === 'from_received_materials') {
    materialLineItems = transformReceivedLineItemsToInvoiceLineItems(
      receivedLineItems,
      groupName,
      0,
    );
  } else {
    const lineItemsForInvoice: LoadLineItemForInvoice[] = shippedLineItems.map(
      (li) => ({
        id: li.id,
        itemId: li.itemId ?? null,
        itemName: li.item?.name ?? 'Unknown Item',
        weights: li.weights ?? null,
        unitPrice: li.unitPrice ?? null,
        pricingUnit: li.pricingUnit ?? null,
      }),
    );
    materialLineItems = transformLoadLineItemsToInvoiceLineItems(
      lineItemsForInvoice,
      groupName,
      0,
    );
  }

  const currencyCode =
    materialLineItems.length > 0
      ? materialLineItems[0].totalPrice.currencyCode
      : enumCurrencyCode.USD;

  let nextSortOrder = materialLineItems.length;

  const deductionLineItems = transformDeductionsToInvoiceLineItems(
    deductions,
    deductionTypesMap,
    currencyCode,
    nextSortOrder,
  );
  nextSortOrder += deductionLineItems.length;

  const freightLineItems = transformFreightChargesToInvoiceLineItems(
    freightCharges,
    currencyCode,
    nextSortOrder,
  );

  return [...materialLineItems, ...deductionLineItems, ...freightLineItems];
}
